import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
    fixBankPageWithAIAsync,
    getAsyncTask,
} from "../../docuclipper/api";
import { ReduxState } from "..";

import { createAlert } from "./Alerts";
import { pollForCompletion } from "./AsyncUtils";

/**
 * Usage example:
 * 
 * import { useSelector, useDispatch } from 'react-redux';
 * import { fixBankPageActionAsync } from '../redux/reducers/FixBankPageAsync';
 * 
 * const MyComponent = () => {
 *   const dispatch = useDispatch();
 *   const { loading } = useSelector((state: ReduxState) => state.FixBankPageAsync);
 *   
 *   const handleFixPage = () => {
 *     dispatch(fixBankPageActionAsync({ documentId: 123, pageNumber: 1 }));
 *   };
 *   
 *   return (
 *     <button onClick={handleFixPage} disabled={loading}>
 *       {loading ? 'Fixing...' : 'Fix Bank Page with AI'}
 *     </button>
 *   );
 * };
 */

const initialState: {
    loading: boolean;
    asyncJobId: string | null;
} = {
    loading: false,
    asyncJobId: null,
};

const slice = createSlice({
    name: "FixBankPageAsync",
    initialState,
    reducers: {
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        setAsyncJobId(state, action: PayloadAction<string>) {
            state.asyncJobId = action.payload;
        },
    },
});

export const { setAsyncJobId, setLoading } = slice.actions;

export const fixBankPageActionAsync = ({
    documentId,
    pageNumber
}: {
    documentId: number;
    pageNumber: number;
}) => async (dispatch, getState) => {
    const state: ReduxState = getState();
    if (state.FixBankPageAsync.loading) {
        return;
    }

    try {
        dispatch(setLoading(true));

        const res = await fixBankPageWithAIAsync(documentId, pageNumber);
        const asyncJobId = res.asyncJobId;

        dispatch(setAsyncJobId(asyncJobId));

        const onSuccess = async () => {
            dispatch(setLoading(false));
            dispatch(
                createAlert({
                    message: "Bank page fixed successfully with AI",
                    timeout: 0,
                    type: "success",
                })
            );

            // Purge the infinite cache to refresh the grid data
            const currentState: ReduxState = getState();
            const gridApi = currentState.TransactionManager.gridApi;
            if (gridApi) {
                gridApi.purgeInfiniteCache();
            }
        };

        const onFail = async () => {
            dispatch(setLoading(false));
            dispatch(
                createAlert({
                    message: "Error fixing bank page with AI. Please try again.",
                    timeout: 0,
                    type: "error",
                })
            );
        };

        pollForCompletion(dispatch, asyncJobId, null, onSuccess, onFail);
    } catch (err: unknown) {
        dispatch(setLoading(false));
        dispatch(createAlert({
            message: (err as Error).message || 'Error fixing bank page',
            timeout: 0,
            type: 'error'
        }));
    }
};

export default slice.reducer; 